import { type IProposalCartItem, NegotiationType } from '@/types/commercial/cart';
import { originalOrUnit, subtotalOrDiscountedFallback } from '@/utils/commercial/cart-price';

export type CartSummaryPaymentType = 'CASH' | 'INSTALLMENTS';

export type CartSummaryCategory = {
  grossInCents: number;
  netInCents: number;
  discountInCents: number;
};

export type CartSummaryCategories = {
  products: CartSummaryCategory;
  accessories: CartSummaryCategory;
  connectivity: CartSummaryCategory;
  monthlyServices: CartSummaryCategory;
  yearlyServices: CartSummaryCategory;
  fixedServices: CartSummaryCategory;
};

const emptyCategory = (): CartSummaryCategory => ({
  grossInCents: 0,
  netInCents: 0,
  discountInCents: 0,
});

function addCategoryValue(category: CartSummaryCategory, grossInCents: number, netInCents: number) {
  category.grossInCents += grossInCents;
  category.netInCents += netInCents;
  category.discountInCents += Math.max(0, grossInCents - netInCents);
}

function capexGrossAndNet(item: IProposalCartItem, isCash: boolean) {
  const unitOriginal = isCash
    ? originalOrUnit(item.originalPriceCashInCents, item.unitPriceCashInCents)
    : originalOrUnit(item.originalPriceInstallmentInCents, item.unitPriceInstallmentInCents);
  return {
    gross: unitOriginal * item.quantity,
    net: subtotalOrDiscountedFallback(
      isCash ? item.subtotalCashInCents : item.subtotalInstallmentInCents,
      unitOriginal,
      item.quantity,
      item.discountPercent,
    ),
  };
}

function recurringGrossAndNet(
  item: IProposalCartItem,
  originalInCents: number,
  unitInCents: number,
  subtotalInCents: number,
  discountPercent: number,
) {
  const unitOriginal = originalOrUnit(originalInCents, unitInCents);
  return {
    gross: unitOriginal * item.quantity,
    net: subtotalOrDiscountedFallback(
      subtotalInCents,
      unitOriginal,
      item.quantity,
      discountPercent,
    ),
  };
}

export function calculateCartSummaryCategories(
  items: IProposalCartItem[],
  paymentType: CartSummaryPaymentType,
): CartSummaryCategories {
  const categories: CartSummaryCategories = {
    products: emptyCategory(),
    accessories: emptyCategory(),
    connectivity: emptyCategory(),
    monthlyServices: emptyCategory(),
    yearlyServices: emptyCategory(),
    fixedServices: emptyCategory(),
  };

  const isCash = paymentType === 'CASH';

  for (const item of items) {
    const nt = item.negotiationType;
    const isProduct =
      nt === NegotiationType.SALE_WITH_SIM_CARD || nt === NegotiationType.SALE_WITHOUT_SIM_CARD;
    const hasConnectivity =
      nt === NegotiationType.SALE_WITH_SIM_CARD || nt === NegotiationType.SIM_CARD;

    if (isProduct) {
      const { gross, net } = capexGrossAndNet(item, isCash);
      addCategoryValue(categories.products, gross, net);
    }

    if (nt === NegotiationType.ACCESSORY_SALE) {
      const { gross, net } = capexGrossAndNet(item, isCash);
      addCategoryValue(categories.accessories, gross, net);
    }

    if (hasConnectivity) {
      const discountPct =
        item.negotiationType === NegotiationType.SALE_WITH_SIM_CARD
          ? item.connectivityDiscountPercent
          : item.discountPercent;
      const { gross, net } = recurringGrossAndNet(
        item,
        item.originalPriceMonthlyInCents,
        item.unitPriceMonthlyInCents,
        item.subtotalMonthlyInCents,
        discountPct,
      );
      addCategoryValue(categories.connectivity, gross, net);
    }

    if (nt === NegotiationType.SERVICES) {
      const monthly = recurringGrossAndNet(
        item,
        item.originalPriceMonthlyInCents,
        item.unitPriceMonthlyInCents,
        item.subtotalMonthlyInCents,
        item.discountPercent,
      );
      addCategoryValue(categories.monthlyServices, monthly.gross, monthly.net);

      const yearly = recurringGrossAndNet(
        item,
        item.originalPriceYearlyInCents,
        item.unitPriceYearlyInCents,
        item.subtotalYearlyInCents,
        item.discountPercent,
      );
      addCategoryValue(categories.yearlyServices, yearly.gross, yearly.net);

      const fixed = recurringGrossAndNet(
        item,
        item.originalPriceFixedInCents,
        item.unitPriceFixedInCents,
        item.subtotalFixedInCents,
        item.discountPercent,
      );
      addCategoryValue(categories.fixedServices, fixed.gross, fixed.net);
    }
  }

  return categories;
}
